import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { Button, InlineStack, Banner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { fetchCustomer } from "containers/customer/customer-container.slice";
import { fetchFinanceDetailRefreshAsync } from "containers/finance-detail/finance-detail-container.slice";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import UserSelectByPermissions from "features/user-select-by-permission/user-select-by-permission";
import { uploadToDokobit } from "services/upload-file.service";
import { signerEditErrorKey } from "utils/signer-edit-errors";
import { fetchFileDetails } from "containers/file/file.Slice";
import UserSelect from "features/user-select/user-select";
import { fetchOrderDetails } from "containers/order-view-container/order-container.slice";
import { useParams } from "react-router-dom";

interface ConfirmModalProps {
  companyId: string;
  customerId: string;
  fileId?: string;
  relatedTo?: string;
  permissionUserSigner?: string | { id?: string };
  userSigner?: string | { id?: string };
  subject?: string;
  year?: number;
  financeId?: string;
  // Runs after the request was sent, e.g. so a list outside redux can refresh.
  onSuccess?: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  companyId,
  customerId,
  fileId,
  relatedTo,
  permissionUserSigner,
  userSigner,
  subject,
  year,
  financeId,
  onSuccess,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { orderId } = useParams();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const permissionId =
    typeof permissionUserSigner === "string"
      ? permissionUserSigner
      : (permissionUserSigner as any)?.id ?? "";
  const userSignerId =
    typeof userSigner === "string" ? userSigner : (userSigner as any)?.id ?? "";

  const [selectedUser, setSelectedUser] = useState(permissionId);
  const [selectedNonTechUser, setSelectedNonTechUser] = useState(userSignerId);

  useEffect(() => {
    setSelectedUser(permissionId);
    setSelectedNonTechUser(userSignerId);
  }, [permissionId, userSignerId]);

  const handleDokobitFile = async () => {
    if (!isFormValid()) {
      return;
    }
    try {
      setIsLoading(true);
      const payload = {
        fileId,
        companyUserSigner: selectedUser,
        signType: "Dokobit",
        nontechuser: selectedNonTechUser,
        subject,
      };
      await uploadToDokobit(companyId, payload);
      if (relatedTo) {
        dispatch(fetchFileDetails({ fileId: relatedTo, companyId }));
      } else if (fileId && companyId) {
        dispatch(fetchFileDetails({ fileId, companyId }));
      }

      dispatch(fetchCustomer({ customerId, companyId: companyId! }));

      // Refresh finance detail if year is provided
      if (year) {
        dispatch(fetchFinanceDetailRefreshAsync({
          companyId,
          customerId,
          year,
          financeId,
        }));
      }

      if (companyId && orderId) {
        dispatch(fetchOrderDetails({ companyId, orderId }));
      }
      onSuccess?.();
      setSuccessMessage("ok");
      setIsLoading(false);
      closeModal();
    } catch (e: any) {
      setErrMsg(signerEditErrorKey(e, "error"));
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  const isFormValid = () => {
    if (selectedUser === selectedNonTechUser) {
      setErrMsg("duplicate_signer_selected_choose_a_different_user");
      return false;
    }
    return true;
  };

  return (
    <>
      <div className="form-warnings">
        {errMsg && (
          <div>
            <Banner tone="critical">{t(errMsg)}</Banner>
          </div>
        )}
        {successMessage && (
          <div>
            <Banner tone="info">{t(successMessage)}</Banner>
          </div>
        )}
      </div>
      <h1 style={{ textAlign: "center" }}>
        {t("Are_you_sure_you_want_to_request_to_sign_file")}
      </h1>
      <div style={{ marginBottom: "10px" }}>
        <UserSelectByPermissions
          value={selectedUser}
          onChange={setSelectedUser}
          isRequired={true}
          Permission={"Signature"}
        />

        <UserSelect
          onChange={setSelectedNonTechUser}
          value={selectedNonTechUser}
          label={"select_user"}
        />
      </div>
      <InlineStack align="space-between">
        <Button
          onClick={handleDokobitFile}
          loading={isLoading}
          disabled={!selectedUser}
        >
          {t("Yes")}
        </Button>

        <Button onClick={closeModal}>{t("No")}</Button>
      </InlineStack>
    </>
  );
};

export default ConfirmModal;
